import * as React from 'react'
import { Navigate } from 'react-router-dom'

import { ArrowLeft, EyeVisible } from '@factorialco/f0-react/icons/app'
import {
  EngagementSurveyDetailTabsDocument,
  EngagementSurveyTemplateDetailDocument,
  EngagementSurveysRootPageDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { buildSurveyHeaderProps } from './ResourceHeaders/surveyHeader'

const SmartSurveysListPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveysListPage')
)
const ClassicSurveysListPage = React.lazy(
  () => import('modules/surveys/pages/Index/SurveysListPage')
)
const RootPage = React.lazy(() => import('modules/engagement_surveys/pages/RootPage'))
const SurveyIndexRedirect = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveyIndexRedirect')
)
const SurveyOverviewPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveyOverviewPage')
)
const SurveyQuestionsPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveyQuestionsPage')
)
const SurveyEditQuestionnairePage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveyEditQuestionnairePage')
)
const SurveyResponsesPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveyResponsesPage')
)
const SurveySmartInsightsPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveySmartInsightsPage')
)
const SurveySettingsPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SurveySettingsPage')
)
const NewSurveyPage = React.lazy(() => import('modules/engagement_surveys/pages/NewSurveyPage'))
const SmartSurveysTemplatesPage = React.lazy(
  () => import('modules/engagement_surveys/pages/SmartSurveysTemplatesPage')
)
const NewSurveyTemplatePage = React.lazy(
  () => import('modules/engagement_surveys/pages/NewSurveyTemplatePage')
)
const TemplateQuestionsPage = React.lazy(() => import('modules/forms/pages/TemplateQuestionsPage'))
const TemplateSettingsPage = React.lazy(() => import('modules/forms/pages/TemplateSettingsPage'))

const smartSurveysRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.engagement.smart_surveys.path,
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: EngagementSurveysRootPageDocument,
            variables: {
              companyId: session.company.id,
            },
          }).map(({ data }) => {
            const canSeeSmartSurveys =
              data?.surveys._policies.sidebarSeeSmartSurveysSubLevelItem.can
            const canManageTemplates = data?.surveys._policies.createSurveyTemplates.can

            return {
              title: i18n.t('surveys.title'),
              subNavigation: canSeeSmartSurveys
                ? [
                    {
                      id: 'navigation.engagement.smart_surveys.overview.fullPath',
                      title: i18n.t('surveys.tabs.overview'),
                      href: navigation.engagement.smart_surveys.overview.fullPath(),
                      index: true,
                    },
                    {
                      id: 'navigation.engagement.smart_surveys.classic_surveys.fullPath',
                      title: i18n.t('surveys.tabs.classic_surveys'),
                      href: navigation.engagement.smart_surveys.classic_surveys.fullPath(),
                    },
                    {
                      id: 'navigation.engagement.smart_surveys.templates.fullPath',
                      title: i18n.t('surveys.tabs.templates'),
                      href: navigation.engagement.smart_surveys.templates.fullPath(),
                      isVisible: canManageTemplates,
                    },
                  ]
                : undefined,
            }
          })
        },
      },
      children: [
        {
          index: true,
          path: '',
          element: (
            <Navigate to={navigation.engagement.smart_surveys.overview.fullPath()} replace />
          ),
        },
        {
          path: navigation.engagement.smart_surveys.new.path,
          element: <NewSurveyPage />,
        },
        {
          path: navigation.engagement.smart_surveys.overview.path,
          element: <SmartSurveysListPage />,
        },
        {
          path: navigation.engagement.smart_surveys.classic_surveys.path,
          element: <ClassicSurveysListPage />,
        },
        {
          path: navigation.engagement.smart_surveys.templates.path,
          children: [
            {
              index: true,
              element: <SmartSurveysTemplatesPage />,
            },
            {
              path: navigation.engagement.smart_surveys.templates.new.path,
              element: <NewSurveyTemplatePage />,
            },
            {
              path: navigation.engagement.smart_surveys.templates.id.path,
              handle: {
                resource: true,
                meta: ({ params, watchQuery }) => {
                  return watchQuery({
                    query: EngagementSurveyTemplateDetailDocument,
                    variables: {
                      id: params.id ?? '',
                    },
                  }).map(({ data }) => {
                    const form = data?.forms.form

                    if (!form) {
                      return { title: '' }
                    }

                    return {
                      title: form.title,
                      headerComponentProps: {
                        title: form.title,
                        description: form.description,
                      },
                      subNavigation: [
                        {
                          title: i18n.t('surveys.tabs.questions'),
                          href: navigation.engagement.smart_surveys.templates.id.questions.fullPath(
                            {
                              id: form.id,
                            }
                          ),
                          index: true,
                        },
                        {
                          title: i18n.t('surveys.tabs.settings'),
                          href: navigation.engagement.smart_surveys.templates.id.settings.fullPath({
                            id: form.id,
                          }),
                        },
                      ],
                    }
                  })
                },
              },
              children: [
                {
                  index: true,
                  element: <Navigate to="questions" replace />,
                },
                {
                  path: navigation.engagement.smart_surveys.templates.id.questions.path,
                  element: <TemplateQuestionsPage />,
                },
                {
                  path: navigation.engagement.smart_surveys.templates.id.settings.path,
                  element: <TemplateSettingsPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.engagement.smart_surveys.survey.path,
          handle: {
            resource: true,
            meta: ({ watchQuery, params }) => {
              const surveyId = params.surveyId

              return watchQuery({
                query: EngagementSurveyDetailTabsDocument,
                variables: {
                  surveyId: surveyId ?? '',
                },
              }).map((queryResult) => {
                const { data } = queryResult
                const survey = data?.surveys.survey

                if (!survey) {
                  return { title: '' }
                }

                const isPublished = ['active', 'finished', 'archived'].includes(survey.form.status)
                const subNavigation = []

                // Overview tab - only visible when published
                if (isPublished) {
                  subNavigation.push({
                    title: i18n.t('surveys.tabs.overview'),
                    href: navigation.engagement.smart_surveys.survey.overview.fullPath({
                      surveyId: survey.id,
                    }),
                    index: true, // Make it the index/default when visible
                    isVisible: true,
                  })
                }

                // Responses tab - only visible when published (has responses)
                subNavigation.push({
                  title: i18n.t('surveys.tabs.responses'),
                  href: navigation.engagement.smart_surveys.survey.responses.fullPath({
                    surveyId: survey.id,
                  }),
                  isVisible: isPublished,
                })

                // Questions tab - always visible
                subNavigation.push({
                  title: i18n.t('surveys.tabs.questions'),
                  href: navigation.engagement.smart_surveys.survey.questions.fullPath({
                    surveyId: survey.id,
                  }),
                  index: !isPublished, // Make it the index/default when overview is not visible
                  isVisible: true,
                })

                // Smart Insights tab
                subNavigation.push({
                  title: i18n.t('surveys.tabs.smart_insights'),
                  href: navigation.engagement.smart_surveys.survey.smartInsights.fullPath({
                    surveyId: survey.id,
                  }),
                  isVisible: false,
                })

                // Settings tab - always visible
                subNavigation.push({
                  title: i18n.t('surveys.tabs.settings'),
                  href: navigation.engagement.smart_surveys.survey.settings.fullPath({
                    surveyId: survey.id,
                  }),
                  isVisible: true,
                })

                return {
                  title: survey.form.title,
                  headerComponentProps: buildSurveyHeaderProps(data),
                  subNavigation,
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <SurveyIndexRedirect />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.questions.path,
              element: <SurveyQuestionsPage />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.overview.path,
              element: <SurveyOverviewPage />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.responses.path,
              element: <SurveyResponsesPage />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.smartInsights.path,
              element: <SurveySmartInsightsPage />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.settings.path,
              element: <SurveySettingsPage />,
            },
            {
              path: navigation.engagement.smart_surveys.survey.edit.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, navigate }) => {
                  const surveyId = params.surveyId

                  return watchQuery({
                    query: EngagementSurveyDetailTabsDocument,
                    variables: { surveyId: surveyId ?? '' },
                  }).map(({ data }) => {
                    const survey = data?.surveys.survey

                    if (!survey) {
                      return { title: '' }
                    }

                    const canEditSurvey = survey._policies?.edit.can

                    if (!canEditSurvey) {
                      navigate(
                        navigation.engagement.smart_surveys.survey.fullPath({
                          surveyId: surveyId ?? '',
                        })
                      )
                      return { title: '' }
                    }

                    return {
                      title: i18n.t('surveys.edit_mode.title'),
                      headerComponentProps: {
                        title: survey.form.title,
                        primaryAction: {
                          key: 'save',
                          label: i18n.t('common.save'),
                        },
                        secondaryActions: [
                          {
                            key: 'back',
                            label: i18n.t('surveys.header.actions.back_to_overview'),
                            icon: ArrowLeft,
                          },
                          {
                            key: 'preview',
                            label: i18n.t('common.preview'),
                            icon: EyeVisible,
                          },
                        ],
                      },
                    }
                  })
                },
              },
              element: <SurveyEditQuestionnairePage />,
            },
          ],
        },
      ],
    },
  ]
}

export default smartSurveysRoutes
